import { useState, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, FormControl } from '@mui/material';
import RequestsList from '../../../components/RequestsList';
import Label from '../../../components/Label';
import { useRequestTypesContext } from '../../../contexts/RequestTypesContext';
import { dashboardCardSx } from '../../../styles/requestFormShell';
import {
  getBaseFormControlStyles,
  getBaseTextFieldStyles,
} from '../../../styles/fieldStyles';

const filterFieldSx = {
  flex: { md: '1 1 0', xs: '1 1 auto' },
  minWidth: { md: 0, xs: '100%' },
};

const RequestHistoryPanel = () => {
  const { sortedRequestTypes } = useRequestTypesContext();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(1);
  const [selectedRequestType, setSelectedRequestType] = useState(0);

  const timePeriods = [
    { label: 'Last 24 Hours', value: 1 },
    { label: 'Last 7 Days', value: 7 },
    { label: 'Last 30 Days', value: 30 },
  ];

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  return (
    <Box
      aria-label="Request history"
      component="section"
      sx={{
        ...dashboardCardSx,
        flex: 1,
        // Without this a flex child refuses to shrink below its content, and the
        // table cannot claim the leftover space.
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid rgba(22, 56, 96, 0.12)',
          display: 'flex',
          flexDirection: { md: 'row', xs: 'column' },
          gap: 2,
          p: 2.5,
        }}
      >
        <Box sx={filterFieldSx}>
          <Label htmlFor="request-history-account-id" text="Account ID" />
          <TextField
            fullWidth
            id="request-history-account-id"
            placeholder="Enter account ID…"
            size="small"
            value={searchText}
            variant="outlined"
            onChange={(e) => setSearchText(e.target.value)}
            sx={getBaseTextFieldStyles('100%')}
          />
        </Box>

        <Box sx={filterFieldSx}>
          <Label htmlFor="request-history-time-period" text="Time Period" />
          <FormControl
            fullWidth
            size="small"
            sx={getBaseFormControlStyles('100%')}
          >
            <Select
              displayEmpty={false}
              id="request-history-time-period"
              value={selectedTimePeriod}
              onChange={(e) => setSelectedTimePeriod(Number(e.target.value))}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 280 },
                },
              }}
              sx={{ textAlign: 'left' }}
            >
              {timePeriods.map((period) => (
                <MenuItem key={period.value} value={period.value}>
                  {period.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={filterFieldSx}>
          <Label htmlFor="request-history-request-type" text="Request Type" />
          <FormControl
            fullWidth
            size="small"
            sx={getBaseFormControlStyles('100%')}
          >
            <Select
              displayEmpty={false}
              id="request-history-request-type"
              value={selectedRequestType}
              onChange={(e) => setSelectedRequestType(Number(e.target.value))}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 280 },
                },
              }}
              sx={{ textAlign: 'left' }}
            >
              <MenuItem value={0}>
                <em>All</em>
              </MenuItem>
              {sortedRequestTypes.map((requestType) => (
                <MenuItem key={requestType.id} value={requestType.id}>
                  {requestType.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <RequestsList
        searchText={debouncedSearchText}
        selectedTimePeriod={selectedTimePeriod}
        selectedRequestType={selectedRequestType}
      />
    </Box>
  );
};

export default RequestHistoryPanel;
